/**
 * KISSED University · Admissions Quiz (5 questions).
 * Route: /university/quiz
 *
 * Computes a recommendation locally and persists answers + recommendation in
 * sessionStorage under QUIZ_SESSION_KEY. Navigates to /university/recommendation
 * (or /university/herbal-waitlist when the user picks the Herbal pathway).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import {
  QUESTIONS,
  computeRecommendation,
  persistQuizState,
  startMonthFromAnswer,
} from "./quizData";

export default function UniversityQuiz() {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});

  const question = QUESTIONS[idx];
  const total = QUESTIONS.length;
  const selected = answers[question.id] || null;

  // Reset scroll on each question.
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [idx]);

  const pickOption = (value) => {
    setAnswers((a) => ({ ...a, [question.id]: value }));
  };

  const canAdvance = useMemo(() => Boolean(selected), [selected]);
  const isLast = idx === total - 1;

  const goNext = () => {
    if (!canAdvance) return;
    if (!isLast) { setIdx((i) => i + 1); return; }

    const rec = computeRecommendation(answers);
    const state = {
      answers,
      recommendation: rec,
      desired_start_month: startMonthFromAnswer(answers.q5),
      created_at: new Date().toISOString(),
    };
    persistQuizState(state);

    if (rec === "HERBAL") {
      navigate("/university/herbal-waitlist");
    } else {
      navigate("/university/recommendation");
    }
  };

  const goBack = () => { if (idx > 0) setIdx((i) => i - 1); };

  return (
    <div data-testid="university-quiz" className="min-h-screen bg-[#faf7f5] flex flex-col">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/university" className="flex items-center gap-3">
            <img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" />
            <span className="font-display text-sm text-slate-700">KISSED University · Pathway Quiz</span>
          </Link>
          <span className="text-xs text-slate-500" data-testid="quiz-progress-count">
            Question {idx + 1} of {total}
          </span>
        </div>
        <div className="h-1 bg-slate-100">
          <div
            className="h-1 bg-purple-900 transition-all"
            style={{ width: `${((idx + 1) / total) * 100}%` }}
            data-testid="quiz-progress-bar"
          />
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 py-12">
        <div className="text-[10px] uppercase tracking-[0.22em] text-purple-900 font-semibold mb-3">
          Finding Your Pathway · Question {idx + 1}
        </div>
        <h1
          className="font-display text-2xl sm:text-3xl text-slate-900 leading-tight mb-8"
          data-testid="quiz-question"
        >
          {question.text}
        </h1>

        <div className="space-y-3" data-testid="quiz-options">
          {question.options.map((opt) => {
            const on = selected === opt.value;
            const tid = `quiz-opt-${question.id}-${opt.value.replace(/\W+/g, "-").toLowerCase()}`;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => pickOption(opt.value)}
                data-testid={tid}
                className={`w-full text-left flex items-center gap-4 px-4 py-3.5 rounded-2xl border transition ${
                  on
                    ? "border-purple-900 bg-purple-50"
                    : "border-slate-200 bg-white hover:border-purple-400 hover:bg-purple-50/40"
                }`}
              >
                <span
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition ${
                    on ? "border-purple-900 bg-purple-900" : "border-slate-300"
                  }`}
                  aria-hidden="true"
                >
                  <span className={`w-2 h-2 rounded-full bg-white transition ${on ? "opacity-100" : "opacity-0"}`} />
                </span>
                <span className="text-sm text-slate-900">{opt.label}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-10 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={goBack}
            disabled={idx === 0}
            className="text-slate-600"
            data-testid="quiz-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button
            onClick={goNext}
            disabled={!canAdvance}
            className="bg-purple-900 hover:bg-purple-800 text-white rounded-full px-7"
            data-testid="quiz-next-btn"
          >
            {isLast ? "See My Pathway" : "Next"}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </main>
    </div>
  );
}
